<template>
  <v-card-text class="card-content">
    <!-- Modernes Uhren-Widget -->
    <div class="clock-card mb-4">
      <div class="glass-card-grid">
        <div class="time-card">
          <div class="time-label">Uhrzeit</div>
          <div class="time-display">{{ formatTimeDisplay(currentTime) }}</div>
        </div>

        <div class="date-card">
          <div class="date-label">Datum</div>
          <div class="date-display">{{ currentDate }}</div>
        </div>

        <div class="week-card">
          <div class="week-label">Kalenderwoche</div>
          <div class="week-display">KW {{ weekNumber }}</div>
        </div>
      </div>
    </div>

    <!-- Begrüßung des aktuellen Nutzers -->
    <div class="welcome-message pa-2 text-center mb-4">
      <span class="welcome-text">
        Willkommen, {{ dashboardData.user.vorname || "Gast" }}!
      </span>
    </div>

    <!-- Aktive Nutzer mit neuem List-Layout -->
    <div class="active-users-section">
      <div class="d-flex justify-space-between align-center mb-3">
        <h3 class="subsection-title mb-0">
          <v-icon size="small" color="success" class="mr-1"
            >mdi-account-multiple</v-icon
          >
          Aktive Benutzer
        </h3>
        <v-btn
          icon
          size="x-small"
          variant="text"
          color="success"
          @click="refreshActiveUsers"
          :disabled="loading"
          title="Aktualisieren"
        >
          <v-icon size="small" :class="{ rotate: loading }">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="success"
        class="mb-2"
      ></v-progress-linear>

      <!-- Schlankere User-Liste -->
      <div class="users-list">
        <div
          v-for="user in activeUsers"
          :key="user.id"
          class="user-item"
          :class="[`status-${user.user_status}`]"
        >
          <div class="user-content">
            <div class="user-avatar-section">
              <v-avatar size="32" class="user-avatar" color="grey-darken-3">
                <span class="avatar-text">{{
                  getInitials(user.vorname, user.nachname)
                }}</span>
              </v-avatar>
              <div
                class="user-status-indicator"
                :class="getStatusColor(user.user_status)"
              ></div>
            </div>

            <div class="user-info">
              <div class="d-flex align-center">
                <div class="user-name">
                  {{ user.vorname || "" }} {{ user.nachname || "" }}
                </div>
                <!-- Kompakte Rollen-Icons neben dem Namen -->
                <div class="user-roles">
                  <v-icon
                    v-if="user.admin || user.super_admin"
                    size="x-small"
                    color="error"
                    class="role-icon"
                    title="Administrator"
                  >mdi-shield-account</v-icon>
                  <v-icon
                    v-if="user.developer"
                    size="x-small"
                    color="yellow"
                    class="role-icon"
                    title="Developer"
                  >mdi-code-braces</v-icon>
                  <v-icon
                    v-if="user.monteur"
                    size="x-small"
                    color="success"
                    class="role-icon"
                    title="Monteur"
                  >mdi-tools</v-icon>
                  <v-icon
                    v-if="user.lagerist"
                    size="x-small"
                    color="warning"
                    class="role-icon"
                    title="Lagerist"
                  >mdi-package-variant</v-icon>
                  <v-icon
                    v-if="user.returen_sorting"
                    size="x-small"
                    color="info"
                    class="role-icon"
                    title="Retouren Sortierung"
                  >mdi-package-variant-closed</v-icon>
                  <v-icon
                    v-if="user.office_user"
                    size="x-small"
                    color="blue"
                    class="role-icon"
                    title="Office"
                  >mdi-briefcase</v-icon>
                  <v-icon
                    v-if="user.refurbish_user"
                    size="x-small"
                    color="purple"
                    class="role-icon"
                    title="Refurbish"
                  >mdi-recycle</v-icon>
                  <v-icon
                    v-if="user.inventur_user || user.inventur"
                    size="x-small"
                    color="teal"
                    class="role-icon"
                    title="Inventur"
                  >mdi-clipboard-list-outline</v-icon>
                </div>
              </div>
              <div class="user-details">
                <span class="user-status-text">
                  <template
                    v-if="
                      user.user_status === 'custom' &&
                      user.custom_status_message
                    "
                  >
                    {{ user.custom_status_message }}
                  </template>
                  <template v-else>{{
                    getStatusText(user.user_status)
                  }}</template>
                </span>

                <span v-if="user.mitarbeiternummer" class="user-id">
                  • #{{ user.mitarbeiternummer }}
                </span>

                <span v-if="user.abteilung" class="user-department">
                  • {{ user.abteilung }}
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
              <!-- E-Mail-Button -->
              <v-btn
                v-if="user.email"
                icon="mdi-email-outline"
                size="x-small"
                color="primary"
                variant="text"
                class="email-btn"
                title="E-Mail senden"
                @click="openEmail(user.email)"
              ></v-btn>

              <!-- Chat-Button -->
              <v-btn
                icon="mdi-chat-outline"
                size="x-small"
                color="success"
                variant="text"
                class="chat-btn"
                title="Privaten Chat öffnen"
                @click="openPrivateChat(user)"
              ></v-btn>
            </div>
          </div>
        </div>

        <div v-if="activeUsers.length === 0" class="no-users-message">
          <v-icon size="large" color="grey-darken-1" class="mb-2"
            >mdi-account-off</v-icon
          >
          <div>Keine aktiven Benutzer</div>
        </div>
      </div>
    </div>
  </v-card-text>
</template>

<script setup>
import { inject, ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { API_BASE_URL, WS_URL } from "@/config";

// Router für die Navigation zum Chat
const router = useRouter();

// Inject benötigte Daten und Funktionen
const currentTime = inject("currentTime");
const dashboardData = inject("dashboardData");
const weekNumber = inject("weekNumber");
const formatTimeDisplay = inject("formatTimeDisplay");

// Lokale Zustandsvariablen
const activeUsers = ref([]);
const loading = ref(false);

// Computed Properties
const currentDate = computed(() =>
  currentTime.value.toLocaleDateString("de-DE")
);

// Funktion zum Öffnen eines privaten Chats
const openPrivateChat = (user) => {
  router.push({
    path: "/chat",
    query: { userId: user.id },
  });
};

// Funktion zum Öffnen des E-Mail-Programms
const openEmail = (email) => {
  if (email) {
    window.location.href = `mailto:${email}`;
  }
};

// Prüfen, ob ein Benutzer Rollen hat
const hasRoles = (user) => {
  return (
    user.admin ||
    user.super_admin ||
    user.developer ||
    user.monteur ||
    user.lagerist ||
    user.returen_sorting ||
    user.office_user ||
    user.refurbish_user
  );
};

// Aktive Nutzer vom Server laden
const sortActiveUsers = (list) => {
  const statusOrder = {
    online: 1,
    away: 2,
    busy: 3,
    custom: 4,
    mobile: 5,
    offline: 6,
  };

  return [...list].sort((a, b) => {
    const statusA = statusOrder[a.user_status] ?? 99;
    const statusB = statusOrder[b.user_status] ?? 99;
    if (statusA !== statusB) {
      return statusA - statusB;
    }
    const nameA = `${a.vorname || ""} ${a.nachname || ""}`.trim();
    const nameB = `${b.vorname || ""} ${b.nachname || ""}`.trim();
    return nameA.localeCompare(nameB, "de");
  });
};

const fetchActiveUsers = async (force = false) => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}activeUsers.php?action=get_active_users`, {
      withCredentials: true,
    });
    if (response.data.status === "success") {
      const newUsers = response.data.users || [];

      if (!force && activeUsers.value.length > 0) {
        // Merge: frische WebSocket-Status (<5s) behalten, sonst Serverstand
        newUsers.forEach((newUser) => {
          const existing = activeUsers.value.find((u) => u.id === newUser.id);
          if (!existing) {
            return;
          }

          const lastUpdate = existing._lastStatusUpdate || 0;
          const timeSinceUpdate = Date.now() - lastUpdate;

          if (timeSinceUpdate < 5000) {
            newUser.user_status = existing.user_status;
            newUser.custom_status_message = existing.custom_status_message;
            newUser._lastStatusUpdate = lastUpdate;
          }
        });
      }

      // Immer Server-Liste übernehmen (entfernt Offline) und neu sortieren
      activeUsers.value = sortActiveUsers(newUsers);
    }
  } catch (error) {
    console.error("Fehler beim Laden der aktiven Benutzer:", error);
  } finally {
    loading.value = false;
  }
};

const onUserStatusUpdate = (data) => {
  const normalizedUser = {
    id: data.user.id,
    status: data.user.status,
    custom_status_message: data.user.custom_status_message,
  };
  handleStatusUpdate(normalizedUser);
};

// WebSocket für Echtzeit-Updates - nutzt zentrale Verbindung
const setupWebSocket = async () => {
  try {
    const WebSocketService = (await import("@/services/WebSocketService")).default;

    WebSocketService.connect();
    WebSocketService.addMessageHandler("user_status_update", onUserStatusUpdate);
    WebSocketService.addMessageHandler("status_update_confirmed", onUserStatusUpdate);

    if (!WebSocketService.isConnected.value) {
      WebSocketService.connect();
    }
  } catch (error) {
    console.error("Fehler beim Einrichten der WebSocket-Verbindung:", error);
  }
};

// Status-Update-Handling
const handleStatusUpdate = (updatedUser) => {
  const index = activeUsers.value.findIndex(
    (user) => user.id === updatedUser.id
  );

  // Wenn der Benutzer offline geht, entfernen wir ihn aus der Liste
  if (updatedUser.status === "offline") {
    if (index !== -1) {
      activeUsers.value.splice(index, 1);
    }
    return;
  }

  // Wenn der Benutzer bereits in der Liste ist, aktualisieren wir seinen Status
  if (index !== -1) {
    const currentInactiveMinutes =
      activeUsers.value[index].inactive_minutes || 0;

    const oldStatus = activeUsers.value[index].user_status;
    activeUsers.value[index].user_status = updatedUser.status;
    activeUsers.value[index].custom_status_message =
      updatedUser.custom_status_message || null;
    activeUsers.value[index]._lastStatusUpdate = Date.now();

    if (updatedUser.status === "online" && oldStatus === "away") {
      activeUsers.value[index].inactive_minutes = 0;
    } else {
      activeUsers.value[index].inactive_minutes = currentInactiveMinutes;
    }

    activeUsers.value = sortActiveUsers(activeUsers.value);
    return;
  }

  // Unbekannter User online → Liste neu laden
  fetchActiveUsers(true);
};

// Aktualisierungsfunktion
const refreshActiveUsers = () => {
  fetchActiveUsers(true);
};

// Hilfsfunktionen
const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return "?";
  return `${firstName ? firstName.charAt(0).toUpperCase() : ""}${
    lastName ? lastName.charAt(0).toUpperCase() : ""
  }`;
};

const getStatusColor = (status) => {
  switch (status) {
    case "online":
      return "success";
    case "away":
      return "amber";
    case "busy":
      return "error";
    case "custom":
      return "info";
    case "mobile":
      return "purple";
    case "offline":
    default:
      return "grey";
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "online":
      return "Online";
    case "away":
      return "Abwesend";
    case "busy":
      return "Beschäftigt";
    case "custom":
      return "Benutzerdefiniert";
    case "mobile":
      return "Mobil";
    case "offline":
    default:
      return "Offline";
  }
};

const formatInactiveTime = (minutes) => {
  if (minutes < 60) {
    return `${minutes} Min.`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} Std.${
    remainingMinutes > 0 ? ` ${remainingMinutes} Min.` : ""
  }`;
};

// Komponenten-Lifecycle
onMounted(() => {
  fetchActiveUsers();
  setupWebSocket();

  // Alle 3 Minuten Aktualisieren
  const intervalId = setInterval(fetchActiveUsers, 3 * 60 * 1000);

  // Bereinigung
  onBeforeUnmount(() => {
    clearInterval(intervalId);
    import("@/services/WebSocketService").then(({ default: WebSocketService }) => {
      WebSocketService.removeMessageHandler("user_status_update", onUserStatusUpdate);
      WebSocketService.removeMessageHandler(
        "status_update_confirmed",
        onUserStatusUpdate
      );
    });
  });
});
</script>

<style scoped>
/* ======== Header-Styling ======== */
.section-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stats-header {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.1),
    transparent
  );
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 16px !important;
}

.subsection-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
}

/* ======== Uhrenlayout mit Glasmorphismus ======== */
.clock-card {
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.glass-card-grid {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr;
  gap: 2px;
}

.time-card,
.date-card,
.week-card {
  padding: 16px;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all 0.3s ease;
}

.time-card {
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.15),
    rgba(30, 30, 40, 0.5)
  );
  border-left: 3px solid rgba(76, 175, 80, 0.5);
}

.time-card:hover,
.date-card:hover,
.week-card:hover {
  background-color: rgba(40, 40, 50, 0.6);
  transform: translateY(-2px);
}

.time-label,
.date-label,
.week-label {
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.7rem;
  opacity: 0.7;
  margin-bottom: 4px;
}

.time-display {
  font-size: 1.6rem;
  letter-spacing: 1px;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.95);
}

.date-display,
.week-display {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
}

/* ======== Begrüßungsnachricht ======== */
.welcome-message {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.15),
    rgba(76, 175, 80, 0.05)
  );
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-left: 3px solid rgba(76, 175, 80, 0.7);
  transition: all 0.3s ease;
}

.welcome-message:hover {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.1)
  );
  transform: translateY(-2px);
}

.welcome-text {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

/* ======== Benutzer-Liste mit schlankeren Elementen ======== */
.users-list {
  background: rgba(30, 30, 40, 0.4);
  border-radius: 10px;
  overflow: hidden;
  margin-top: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.user-item {
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.25s ease;
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background: rgba(35, 35, 45, 0.5);
  transform: translateY(-1px);
}

.user-content {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  position: relative;
}

.user-avatar-section {
  position: relative;
  margin-right: 10px;
  flex-shrink: 0;
}

.avatar-text {
  font-size: 0.8rem;
}

.user-avatar {
  border: 1px solid rgba(76, 175, 80, 0.3);
  transition: all 0.3s ease;
}

.user-item:hover .user-avatar {
  border-color: rgba(76, 175, 80, 0.6);
}

.user-status-indicator {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(25, 25, 30, 0.85);
  bottom: -1px;
  right: -1px;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  margin-right: 6px;
}

.user-roles {
  display: flex;
  gap: 4px;
  margin-left: 4px;
}

.role-icon {
  opacity: 0.85;
  transition: opacity 0.2s ease;
}

.user-item:hover .role-icon {
  opacity: 1;
}

.user-details {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-status-text {
  flex-shrink: 0;
}

.user-department {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 4px;
  font-size: 0.7rem;
  opacity: 0.9;
}

.chat-btn {
  opacity: 0;
  transition: all 0.25s ease;
  margin-left: 6px;
  flex-shrink: 0;
}

.user-item:hover .chat-btn {
  opacity: 0.8;
}

.user-item:hover .chat-btn:hover {
  opacity: 1;
  transform: scale(1.1);
  background: rgba(76, 175, 80, 0.1);
}

/* ======== Status-Styles ======== */
.status-online {
  border-left: 2px solid rgba(76, 175, 80, 0.7);
}

.status-away {
  border-left: 2px solid rgba(255, 193, 7, 0.7);
}

.status-busy {
  border-left: 2px solid rgba(244, 67, 54, 0.7);
}

.status-custom,
.status-mobile {
  border-left: 2px solid rgba(33, 150, 243, 0.7);
}

.success {
  background-color: rgba(76, 175, 80, 1);
}

.amber {
  background-color: rgba(255, 193, 7, 1);
}

.error {
  background-color: rgba(244, 67, 54, 1);
}

.info {
  background-color: rgba(33, 150, 243, 1);
}

.purple {
  background-color: rgba(156, 39, 176, 1);
}

.grey {
  background-color: rgba(158, 158, 158, 1);
}

/* ======== Ladeanimation ======== */
.rotate {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ======== Keine Benutzer Nachricht ======== */
.no-users-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px;
  color: rgba(255, 255, 255, 0.7);
}

/* ======== Responsive Design ======== */
@media (max-width: 960px) {
  .glass-card-grid {
    grid-template-columns: 1fr 1fr;
  }

  .week-card {
    grid-column: span 2;
  }

  .time-display {
    font-size: 1.4rem;
  }
}

@media (max-width: 600px) {
  .time-display {
    font-size: 1.2rem;
  }

  .date-display,
  .week-display {
    font-size: 1rem;
  }

  .welcome-text {
    font-size: 0.95rem;
  }

  .chat-btn,
  .email-btn {
    opacity: 0.7;
  }
}

/* ======== Action Buttons Container ======== */
.action-buttons {
  display: flex;
  gap: 4px;
  align-items: center;
}

/* ======== E-Mail & Chat Button Styling ======== */
.email-btn,
.chat-btn {
  opacity: 0.8;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.email-btn:hover,
.chat-btn:hover {
  opacity: 1;
  transform: scale(1.15);
}

.user-status-text {
  flex-shrink: 0;
}

.user-id {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 4px;
  font-size: 0.7rem;
  opacity: 0.9;
  font-weight: 500;
  color: rgba(76, 175, 80, 0.85); /* Hervorhebung der Mitarbeiternummer in Grün */
}

.user-department {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 4px;
  font-size: 0.7rem;
  opacity: 0.9;
}
</style>
